import React, { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, CheckCircle2, MessageCircle,
  Activity, Radio, Disc3, Crosshair, Wind, Droplets, Truck, Cpu,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import QuoteForm from "@/components/QuoteForm";
import Gallery from "@/components/Gallery";
import { SERVICE_BY_SLUG, SERVICES } from "@/data/services";
import { SITE } from "@/data/site";

const ICONS = { Activity, Radio, Disc3, Crosshair, Wind, Droplets, Truck, Cpu };

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICE_BY_SLUG[slug];

  useEffect(() => {
    if (service) {
      document.title = `${service.title} | DATAVIB`;
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [service]);

  if (!service) return <Navigate to="/" replace />;
  const Icon = ICONS[service.icon] || Activity;

  const others = SERVICES.filter((s) => s.slug !== service.slug).slice(0, 3);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="relative bg-blueprint text-white border-b-4 border-cyan-600 py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            data-testid="service-back-link"
            className="inline-flex items-center gap-2 text-cyan-300 hover:text-cyan-200 text-sm font-medium mb-6"
          >
            <ArrowLeft className="w-4 h-4" /> Tüm hizmetler
          </Link>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 grid place-items-center bg-cyan-600 rounded-sm">
                  <Icon className="w-6 h-6 text-white" strokeWidth={1.5} />
                </div>
                <span className="font-mono-accent text-xs tracking-[0.22em] text-cyan-300">
                  {service.code} / HİZMET
                </span>
              </div>
              <h1
                className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]"
                style={{ fontFamily: "Outfit" }}
              >
                {service.title}
              </h1>
              <p className="mt-5 text-lg text-slate-300 leading-relaxed max-w-2xl">
                {service.short}
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <a
                  href="#hizmet-teklif"
                  data-testid="service-cta-quote"
                  className="btn-primary"
                  style={{ backgroundColor: "#0d9488" }}
                >
                  Bu hizmet için teklif al <ArrowRight className="w-4 h-4" />
                </a>
                <a
                  href={SITE.whatsapp}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="service-cta-whatsapp"
                  className="btn-whatsapp"
                >
                  <MessageCircle className="w-4 h-4" /> WhatsApp'tan Yaz
                </a>
              </div>
            </div>
            <div className="lg:col-span-4">
              <img
                src={service.image}
                alt={service.title}
                className="w-full h-[280px] lg:h-[340px] object-cover rounded-sm border border-slate-700"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-7">
              <div className="overline mb-3">NE SAĞLARIZ</div>
              <h2
                className="text-3xl sm:text-4xl font-bold text-slate-900 mb-8"
                style={{ fontFamily: "Outfit" }}
              >
                Bu hizmet kapsamında ne yapıyoruz?
              </h2>
              <ul className="space-y-4">
                {service.delivers.map((d, i) => (
                  <li
                    key={i}
                    data-testid={`service-deliver-${i}`}
                    className="flex items-start gap-3 p-4 border border-slate-200 rounded-sm bg-slate-50/40"
                  >
                    <CheckCircle2 className="w-5 h-5 text-cyan-600 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 text-sm leading-relaxed">{d}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:col-span-5 space-y-8">
              <div className="ind-card p-7">
                <div className="overline mb-4">HANGİ EKİPMANLARDA?</div>
                <ul className="grid grid-cols-2 gap-2 text-sm">
                  {service.equipment.map((e, i) => (
                    <li
                      key={i}
                      className="px-3 py-2 bg-slate-50 border border-slate-200 text-slate-700 rounded-sm"
                    >
                      {e}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="ind-card p-7 bg-slate-900 text-white">
                <div className="font-mono-accent text-xs text-cyan-400 tracking-[0.22em] uppercase mb-4">
                  Mini Süreç
                </div>
                <ol className="space-y-3 text-sm">
                  {service.process.map((p, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="font-mono-accent text-cyan-400 font-bold">{String(i + 1).padStart(2, "0")}</span>
                      <span className="text-slate-300">{p}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery (boş ise hiç render etmez) */}
      <Gallery serviceKey={service.galleryKey} />

      {/* Quote section */}
      <section
        id="hizmet-teklif"
        className="bg-slate-50 border-y border-slate-200 py-16 lg:py-24"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="overline mb-3">TEKLİF AL</div>
            <h2
              className="text-3xl sm:text-4xl font-bold text-slate-900"
              style={{ fontFamily: "Outfit" }}
            >
              {service.title} için talep oluşturun.
            </h2>
          </div>
          <div className="max-w-3xl mx-auto">
            <QuoteForm defaultService={service.title} />
          </div>
        </div>
      </section>

      {/* Other services */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="overline mb-3">DİĞER HİZMETLER</div>
          <h2
            className="text-2xl sm:text-3xl font-bold text-slate-900 mb-8"
            style={{ fontFamily: "Outfit" }}
          >
            İlgili olabilecek diğer hizmetler
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {others.map((s) => {
              const OIcon = ICONS[s.icon] || Activity;
              return (
                <Link
                  key={s.slug}
                  to={`/hizmetler/${s.slug}`}
                  data-testid={`related-service-${s.slug}`}
                  className="ind-card p-6 card-lift block"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-10 h-10 grid place-items-center bg-slate-900 rounded-sm">
                      <OIcon className="w-5 h-5 text-cyan-400" strokeWidth={1.5} />
                    </div>
                    <span className="font-mono-accent text-[10px] text-slate-400 tracking-[0.22em]">
                      {s.code}
                    </span>
                  </div>
                  <div className="font-bold text-slate-900 mb-2" style={{ fontFamily: "Outfit" }}>
                    {s.title}
                  </div>
                  <div className="text-sm text-slate-600 line-clamp-2">{s.short}</div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
